/**
 * Assessment families, interview types and question sets for the position
 * workspace. Layouts read everything from here: adding an interview type is
 * adding one entry to INTERVIEW_TYPES, not editing a component.
 *
 * `examType` is the backend ExamSet.exam_type value the type maps to. This is a
 * UI mapping only; backend models, routes and fields keep their names.
 */

/** One entry per family. `icon` names an icon in components/ui/Icon. */
export const FAMILIES = {
  ai: { label: 'AI interview', icon: 'ai' },
  coding: { label: 'Coding', icon: 'coding' },
  lang: { label: 'Language', icon: 'lang' },
};

export const FAMILY_ORDER = ['ai', 'coding', 'lang'];

/**
 * Question sets shown in the Question bank rail. There is no question-set entity
 * in the backend: each set is a slice of what exists today.
 *  - `category` matches CampaignQuestion.exam_category ('jd' | 'english').
 *  - `language` narrows a language set to one question language.
 *  - `coding` sets read the campaign coding bank instead.
 *  - `onlyWhenPresent` hides the set when it has no questions.
 */
export const QUESTION_SETS = [
  {
    id: 'jd',
    fam: 'ai',
    name: 'JD questions',
    category: 'jd',
    gen: 'Generate from job description',
    desc: 'Role-fit questions drawn from the job description. The AI interviewer asks follow-ups based on each answer.',
  },
  {
    id: 'code',
    fam: 'coding',
    name: 'Coding problems',
    coding: true,
    gen: 'Generate coding problems',
    desc: 'Auto-graded problems run in the browser IDE. Shared by every coding interview on this position.',
  },
  {
    id: 'eng',
    fam: 'lang',
    name: 'English proficiency',
    category: 'english',
    language: 'en',
    gen: 'Generate proficiency test',
    desc: 'Grammar, vocabulary, reading and spoken-fluency questions contextualized to this role. Scored on the CEFR scale.',
  },
  {
    id: 'es',
    fam: 'lang',
    name: 'Spanish proficiency',
    category: 'english',
    language: 'es',
    onlyWhenPresent: true,
    gen: 'Generate proficiency test',
    desc: 'Grammar, vocabulary, reading and spoken-fluency questions in Spanish. Scored on the CEFR scale.',
  },
];

/**
 * Interview types offered by "New interview". Only types the backend can run
 * are listed. `sets` are the question sets the type draws from (first one is
 * the one named on the type card). `legacy` types are recognised on existing
 * interviews but not offered for new ones.
 */
export const INTERVIEW_TYPES = [
  {
    id: 'jd',
    examType: 'jd',
    fam: 'ai',
    name: 'JD-specific exam',
    legacyTitle: 'JD-Specific Exam',
    desc: 'Voice interview built from the job description. Checks role fit.',
    sets: ['jd'],
  },
  {
    id: 'interview_coding',
    examType: 'interview_coding',
    fam: 'ai',
    name: 'AI + coding',
    legacyTitle: 'AI + Coding',
    desc: 'Voice interview followed by a live coding task.',
    sets: ['jd', 'code'],
  },
  {
    id: 'coding',
    examType: 'coding',
    fam: 'coding',
    name: 'Coding exam',
    legacyTitle: 'Coding Exam',
    desc: 'Timed problems in a browser IDE, auto-graded against test cases.',
    sets: ['code'],
  },
  {
    id: 'english',
    examType: 'english',
    fam: 'lang',
    name: 'Language proficiency exam',
    legacyTitle: 'Language-Proficiency Exam',
    desc: 'Grammar, vocabulary, reading and spoken fluency, scored on the CEFR scale.',
    sets: ['eng'],
  },
  {
    id: 'combined',
    examType: 'combined',
    fam: 'ai',
    name: 'JD + Language combined',
    legacyTitle: 'JD + Language Combined',
    desc: 'Earlier combined format, replaced by AI + coding.',
    sets: ['jd', 'eng'],
    legacy: true,
  },
];

export const CATALOG_TYPES = INTERVIEW_TYPES.filter((t) => !t.legacy);

export const typeById = (id) => INTERVIEW_TYPES.find((t) => t.id === id);

/** Interview type for a backend exam_type; unknown/missing falls back to JD. */
export const typeOfExamType = (examType) =>
  INTERVIEW_TYPES.find((t) => t.examType === examType) || INTERVIEW_TYPES[0];

export const familyOfExamType = (examType) => typeOfExamType(examType).fam;

export const setById = (id) => QUESTION_SETS.find((s) => s.id === id);
